// frontend/src/components/ItemWebChannelSrrList.js
import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/apiService';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import { srrStatusLabel, srrPublicationLabel } from './SrrSyncStatus';
import { usePerms } from '../hooks/useAuth';

const ItemWebChannelSrrList = () => {
  const { has } = usePerms();
  const [items, setItems] = useState([]);
  const [srrStatus, setSrrStatus] = useState({}); // itemId -> true/false
  const [syncStatus, setSyncStatus] = useState({});
  const [loading, setSrrading] = useState(true);
  const [loadingStatus, setSrradingStatus] = useState(false);
  const [error, setError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25;

  // Filter-State (analog zu ItemList, aber ohne Export-Flags)
  const [filters, setFilters] = useState({
    name: null,
    productGroup: null,
    motorType: null,
    machineType: null,
    itemType: null,
    deactivated: null,
    foreignArticle: null,
    loActive: null, // clientseitiger Filter: SRR aktiv / nicht aktiv
  });

  // Serverseitige Filteroptionen
  const [serverFilterOptions, setServerFilterOptions] = useState({
    names: [],
    productGroups: [],
    motorTypes: [],
    machineTypes: [],
    itemTypes: [],
    deactivated: [
      { value: true, label: 'Ja' },
      { value: false, label: 'Nein' },
    ],
    foreignArticle: [
      { value: true, label: 'Ja' },
      { value: false, label: 'Nein' },
    ],
  });

  // Dropdown-Optionen für SRR-Status (clientseitig)
  const loActiveOptions = [
    { value: true, label: 'SRR aktiv' },
    { value: false, label: 'SRR nicht aktiv' },
  ];

  // Filteroptionen vom Server laden
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await api.get('/items/filter-options');
        setServerFilterOptions((prev) => ({
          ...prev,
          ...response.data,
        }));
      } catch (err) {
        console.error('Fehler beim Laden der Filteroptionen:', err);
      }
    };

    fetchFilterOptions();
  }, []);

  // Bei Filterwechsel immer auf Seite 1 springen
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', currentPage);

    if (filters.name) {
      params.append('name', filters.name.value);
    }
    if (filters.productGroup) {
      params.append('productGroup', filters.productGroup.value);
    }
    if (filters.motorType) {
      params.append('motorType', filters.motorType.value);
    }
    if (filters.machineType) {
      params.append('machineType', filters.machineType.value);
    }
    if (filters.itemType) {
      params.append('itemType', filters.itemType.value);
    }
    if (filters.deactivated !== null) {
      params.append('deactivated', filters.deactivated.value);
    }
    if (filters.foreignArticle !== null) {
      params.append('foreignArticle', filters.foreignArticle.value);
    }

    // loActive NICHT an den Server schicken, das filtern wir clientseitig
    return `?${params.toString()}`;
  };

  // 1) Items laden (mit serverseitigen Filtern & Pagination)
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setSrrading(true);
        setError(null);

        const query = buildQuery();
        const res = await api.get(`/items/list${query}`);

        const data = res.data.data || res.data;
        const pages = res.data.pages || 1;

        setItems(data);
        setTotalPages(pages);

        // direkt danach SRR-Status laden
        loadSrrStatus(data);
      } catch (err) {
        console.error('Fehler beim Laden der Items für SRR-Liste:', err);
        setError('Fehler beim Laden der Items.');
      } finally {
        setSrrading(false);
      }
    };

    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    filters.name,
    filters.productGroup,
    filters.motorType,
    filters.machineType,
    filters.itemType,
    filters.deactivated,
    filters.foreignArticle,
  ]);
  // loActive bewusst nicht drin: nur clientseitig

  // 2) Für alle Items der aktuellen Seite den SRR-Status nachladen
  const loadSrrStatus = async (itemsForPage) => {
    if (!itemsForPage || itemsForPage.length === 0) {
      setSrrStatus({});
      return;
    }

    setSrradingStatus(true);

    try {
      const results = await Promise.all(
        itemsForPage.map((it) =>
          api
            .get(`/item-web-channels/item/${it.id}`)
            .then((res) => ({ itemId: it.id, channels: res.data || [] }))
            .catch((err) => {
              console.error(
                `Fehler beim Laden der WebChannels für Item ${it.id}:`,
                err
              );
              return { itemId: it.id, channels: [] };
            })
        )
      );

      const statusMap = {};
      results.forEach(({ itemId, channels }) => {
        const lo = channels.find((ch) => ch.channelKey === 'SRR');
        statusMap[itemId] = !!(lo && lo.isActive);
      });

      setSrrStatus(statusMap);
      const sync = await api.get('/srr-integration/statuses', { params: { ids: itemsForPage.map(it => it.id).join(',') } });
      setSyncStatus(Object.fromEntries(sync.data.map(state => [state.source_item_id,state])));
    } catch (err) {
      setError(err.response?.data?.error || 'Synchronisationsstatus konnte nicht geladen werden.');
    } finally {
      setSrradingStatus(false);
    }
  };

  // 3) Clientseitige Filterung nach „SRR aktiv“ auf Basis srrStatus
  const itemsToRender = useMemo(() => {
    if (!filters.loActive) return items;

    const wantActive = filters.loActive.value === true;

    return items.filter((it) => {
      const active = !!srrStatus[it.id];
      return wantActive ? active : !active;
    });
  }, [items, srrStatus, filters.loActive]);

  if (loading) {
    return <div>Lade SRR Web-Channel Liste...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!itemsToRender || itemsToRender.length === 0) {
    return (
      <div>
        <Helmet>
          <title>Channel: SmartRentalReady</title>
        </Helmet>
        <h2>SmartRentalReady.de</h2>
        {has('/srr-integration') && (
          <p><Link to="/srr-integration">Verbindung und Feldfreigaben</Link></p>
        )}
        <p>Keine Maschinen gefunden (mit den aktuellen Filtern).</p>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Channel: SmartRentalReady</title>
        <meta name="description" content="SRR WebChannel Konfiguration" />
      </Helmet>

      <h2>SmartRentalReady.de – Veröffentlichungen</h2>
      <p>
        {has('/srr-integration') && (
          <><Link to="/srr-integration">Verbindung und Feldfreigaben</Link>{' · '}</>
        )}
        <button type="button" onClick={() => loadSrrStatus(items)}>Status aktualisieren</button>
      </p>

      {/* Filterleiste */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Artikel filtern</h3>
        <div className="filter-grid">
          <Select
            options={serverFilterOptions.names.map((val) => ({
              value: val,
              label: val,
            }))}
            placeholder="Name"
            isClearable
            value={filters.name}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, name: selected }))
            }
          />
          <Select
            options={serverFilterOptions.productGroups.map((val) => ({
              value: val,
              label: val,
            }))}
            placeholder="Produktgruppe"
            isClearable
            value={filters.productGroup}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, productGroup: selected }))
            }
          />
          <Select
            options={serverFilterOptions.motorTypes.map((val) => ({
              value: val,
              label: val,
            }))}
            placeholder="Motortyp"
            isClearable
            value={filters.motorType}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, motorType: selected }))
            }
          />
          <Select
            options={serverFilterOptions.machineTypes.map((val) => ({
              value: val,
              label: val,
            }))}
            placeholder="Maschinentyp"
            isClearable
            value={filters.machineType}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, machineType: selected }))
            }
          />
          <Select
            options={serverFilterOptions.itemTypes.map((val) => ({
              value: val,
              label: val,
            }))}
            placeholder="Artikelart"
            isClearable
            value={filters.itemType}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, itemType: selected }))
            }
          />
          <Select
            options={serverFilterOptions.deactivated}
            placeholder="Deaktiviert"
            isClearable
            value={filters.deactivated}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, deactivated: selected }))
            }
          />
          <Select
            options={serverFilterOptions.foreignArticle}
            placeholder="Fremdartikel"
            isClearable
            value={filters.foreignArticle}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, foreignArticle: selected }))
            }
          />

          {/* SRR aktiv (clientseitig) */}
          <Select
            options={loActiveOptions}
            placeholder="SRR aktiv"
            isClearable
            value={filters.loActive}
            onChange={(selected) =>
              setFilters((f) => ({ ...f, loActive: selected }))
            }
          />
        </div>
      </div>

      {loadingStatus && (
        <div style={{ marginBottom: '0.5rem' }}>
          Lade SRR-Status für aktuelle Seite...
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Arbeitshöhe</th>
            <th>Produktgruppe</th>
            <th>Motortyp</th>
            <th>Maschinentyp</th>
            <th>Artikelart</th>
            <th>SRR aktiv</th>
            <th>SmartRentalReady-Synchronisation</th>
            <th>SRR bearbeiten</th>
          </tr>
        </thead>
        <tbody>
          {itemsToRender.map((it) => (
            <tr key={it.id}>
              <td>{it.id}</td>
              <td>{it.name}</td>
              <td>{it.maxWorkingHeight ? `${it.maxWorkingHeight} m` : '-'}</td>
              <td>{it.productGroup?.name || '-'}</td>
              <td>{it.motorType?.name || '-'}</td>
              <td>{it.machineType?.name || '-'}</td>
              <td>{it.itemType || '-'}</td>
              <td>
                {it.id in srrStatus
                  ? srrStatus[it.id]
                    ? 'Ja'
                    : 'Nein'
                  : '-'}
              </td>
              <td>
                <span title={syncStatus[it.id]?.error || syncStatus[it.id]?.connectionError || ''}>{srrStatusLabel(syncStatus[it.id])}<br/>{srrPublicationLabel(syncStatus[it.id]?.response?.publicationStatus)}</span>
              </td>
              <td>
                <Link to={`/itemWebChannelSRR/${it.id}/edit`}>bearbeiten</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={() =>
            setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1))
          }
          disabled={currentPage === 1}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>
          Seite {currentPage} von {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((prev) => prev + 1)}
          disabled={currentPage >= totalPages}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

export default ItemWebChannelSrrList;
